import { useCallback, useEffect, useRef, useState } from 'react';
import { WinnerSelection2Service } from '@services/WinnerSelection2Service';
import { IWinnerMatrix, MatrixCommandResponse } from '../WinnerSelection2.types';
import { errorText, formatMatrixMessage } from '../utils/matrixView';

export type MatrixLoadState = 'loading' | 'ready' | 'error';

export interface ICommandOutcome {
    ok: boolean;
    obsolete?: boolean;
    /** Сообщение для открытого диалога: конфликт или отказ сервера. */
    message?: string;
    warnings: string[];
}

interface IUseWinnerMatrix {
    matrix: IWinnerMatrix | undefined;
    state: MatrixLoadState;
    error: string | undefined;
    /** Команда в полёте — повторные сохранения блокируются. */
    busy: boolean;
    reload: () => void;
    run: (command: () => Promise<MatrixCommandResponse>) => Promise<ICommandOutcome>;
}

/**
 * Данные матрицы по (закупка, tableKey). Смена закупки сбрасывает данные сразу,
 * чтобы действия не оставались доступны на чужой матрице; устаревшие ответы
 * отбрасываются по номеру запроса. Команда возвращает свежую матрицу целиком —
 * она заменяет текущую и при успехе, и при конфликте.
 */
export function useWinnerMatrix(requestKey: string, tableKey: string | undefined): IUseWinnerMatrix {
    const [matrix, setMatrix] = useState<IWinnerMatrix>();
    const [state, setState] = useState<MatrixLoadState>('loading');
    const [error, setError] = useState<string>();
    const [busy, setBusy] = useState(false);
    const busyRef = useRef(false);
    const generation = useRef(0);
    const mounted = useRef(false);
    const controller = useRef<AbortController | null>(null);

    const load = useCallback(
        (keepData: boolean) => {
            controller.current?.abort();
            const current = new AbortController();
            controller.current = current;
            const token = ++generation.current;

            if (!tableKey) {
                setMatrix(undefined);
                setState('error');
                setError('Для компонента не задан параметр tableKey');
                return;
            }

            if (!keepData) setMatrix(undefined);
            setState('loading');
            setError(undefined);
            WinnerSelection2Service.fetchMatrix(requestKey, tableKey, current.signal)
                .then((data) => {
                    if (token !== generation.current || current.signal.aborted) return;
                    setMatrix(data);
                    setState('ready');
                })
                .catch((reason) => {
                    if (token !== generation.current || current.signal.aborted) return;
                    setError(errorText(reason));
                    setState('error');
                });
        },
        [requestKey, tableKey],
    );

    useEffect(() => {
        mounted.current = true;
        load(false);
        return () => {
            mounted.current = false;
            controller.current?.abort();
        };
    }, [load]);

    const reload = useCallback(() => load(true), [load]);

    const run = useCallback(
        async (command: () => Promise<MatrixCommandResponse>): Promise<ICommandOutcome> => {
            if (busyRef.current) return { ok: false, message: 'Предыдущее сохранение ещё выполняется', warnings: [] };
            // Команда по устаревшим или ещё не загруженным данным не отправляется;
            // заодно не прерываем загрузку, которую потом некому было бы завершить.
            if (state !== 'ready')
                return { ok: false, message: 'Дождитесь загрузки актуальных данных и повторите', warnings: [] };
            busyRef.current = true;
            setBusy(true);
            controller.current?.abort();
            const token = ++generation.current;
            try {
                const response = await command();
                if (!mounted.current || token !== generation.current)
                    return { ok: false, obsolete: true, warnings: [] };
                if (response.status === 'invalid') return { ok: false, message: formatMatrixMessage(response.message), warnings: [] };
                setMatrix(response.matrix);
                setState('ready');
                return response.status === 'ok'
                    ? { ok: true, warnings: response.warnings.map(formatMatrixMessage) }
                    : { ok: false, message: formatMatrixMessage(response.message), warnings: [] };
            } catch (reason) {
                if (!mounted.current || token !== generation.current)
                    return { ok: false, obsolete: true, warnings: [] };
                // Исход неизвестен (сеть, таймаут): перечитываем, команду не повторяем.
                reload();
                return { ok: false, message: errorText(reason), warnings: [] };
            } finally {
                busyRef.current = false;
                if (mounted.current) setBusy(false);
            }
        },
        [state, reload],
    );

    return { matrix, state, error, busy, reload, run };
}
